// The search box behind a chip row. Lifted as is from the Andon app.
//
// A chip row holds three answers. The lists behind these questions do not: a
// pod's kit is eighty lines, and the operator could mean any of about five
// hundred MOs, pod types or projects. Ranking decides what is offered without
// tapping; this is what makes the rest reachable.
//
// FILTERED IN THE BROWSER, NOT ON THE SERVER
//
// Both lists are already here — the parts ride along with the question, and
// the five-hundred-row subject list is fetched once and cached across alerts.
// So typing filters an array rather than making a request per keystroke, which
// on a factory network, on a tablet, with gloves on, is the difference between
// a search box and a wait.
//
// It also means the box keeps working when the tablet drops off the network
// mid-answer, which happens.

import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';

export interface SearchRow {
  label: string;
  detail: string | null;
}

/**
 * Substring match over the label and the detail, all terms required.
 *
 * Deliberately not fuzzy. `4774` should find the project and its six types and
 * nothing else; a fuzzy match on a five-hundred-row list returns something for
 * every input, which is worse than returning nothing — an operator who sees a
 * plausible wrong row taps it.
 */
export function filterRows(rows: SearchRow[], term: string, limit = 12): SearchRow[] {
  const words = term.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const out: SearchRow[] = [];
  for (const row of rows) {
    const hay = `${row.label} ${row.detail ?? ''}`.toLowerCase();
    if (words.every((w) => hay.includes(w))) {
      out.push(row);
      if (out.length >= limit) break;
    }
  }
  return out;
}

export function SearchAnswer({
  rows,
  placeholder,
  loading,
  pending,
  onPick,
  onFreeText,
  remote,
}: {
  rows: SearchRow[];
  placeholder: string;
  loading?: boolean;
  pending: boolean;
  onPick: (label: string) => void;
  /**
   * Where to look when `rows` is not the whole list.
   *
   * The ranked rows that travel with a question are a shortlist, and a
   * shortlist a typed word does not match must not read as "no such
   * machine". Called with the term once it is two characters long and has
   * settled for a moment; what comes back is merged under the local hits.
   */
  remote?: (term: string) => Promise<SearchRow[]>;
  /**
   * What to do with text that matched nothing.
   *
   * Still an answer, and still worth sending: the server resolves free text
   * too ("the meriton one", a bare MO number, a pod code), and the list can be
   * a minute stale — a pod released this morning is real even when the cache
   * has not caught up.
   */
  onFreeText: (text: string) => void;
}) {
  const t = useT();
  const [term, setTerm] = useState('');
  const typed = term.trim();
  const [remoteRows, setRemoteRows] = useState<SearchRow[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);

  // Debounced, and guarded against answers arriving out of order: the
  // response to `how` must not land on top of the response to `howick`.
  useEffect(() => {
    if (!remote || typed.length < 2) {
      setRemoteRows([]);
      setRemoteLoading(false);
      return;
    }
    let live = true;
    setRemoteLoading(true);
    const timer = setTimeout(() => {
      remote(typed)
        .then((found) => {
          if (live) setRemoteRows(found);
        })
        .catch(() => {
          if (live) setRemoteRows([]);
        })
        .finally(() => {
          if (live) setRemoteLoading(false);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [remote, typed]);

  // Local hits first, then the register's, never the same label twice.
  const hits = useMemo(() => {
    const local = filterRows(rows, term);
    const seen = new Set(local.map((r) => r.label));
    const extra = filterRows(
      remoteRows.filter((r) => !seen.has(r.label)),
      term,
      Math.max(0, 12 - local.length),
    );
    return [...local, ...extra];
  }, [rows, remoteRows, term]);

  const commit = () => (hits.length === 1 ? onPick(hits[0]!.label) : onFreeText(typed));

  return (
    <div style={{ marginTop: 9 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !typed) return;
            // One hit and Enter means that hit. Otherwise send what they typed
            // and let the server resolve it.
            commit();
          }}
          autoFocus
          maxLength={80}
          placeholder={placeholder}
          aria-label={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            height: 44,
            padding: '0 13px',
            borderRadius: 12,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            color: 'var(--ink)',
            font: '500 13px Manrope,sans-serif',
            outline: 'none',
          }}
        />
        <button
          type="button"
          className="press"
          aria-label={t.saUseTypedT}
          disabled={pending || !typed}
          onClick={commit}
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            display: 'grid',
            placeItems: 'center',
            flex: 'none',
            background: 'var(--accent)',
            color: 'var(--accentInk)',
            opacity: pending || !typed ? 0.45 : 1,
          }}
        >
          <Icon name="send" size={17} />
        </button>
      </div>

      {loading && !rows.length && (
        <p style={{ margin: '8px 2px 0', font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>{t.saLoadingListT}</p>
      )}

      {typed.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {hits.map((row) => (
            <button
              key={row.label}
              type="button"
              className="press"
              disabled={pending}
              onClick={() => onPick(row.label)}
              style={{
                textAlign: 'left',
                minHeight: 44,
                padding: '8px 12px',
                borderRadius: 11,
                background: 'var(--surface2)',
                border: 'none',
                color: 'var(--ink)',
              }}
            >
              <span style={{ display: 'block', font: '600 12.5px Manrope,sans-serif' }}>{row.label}</span>
              {row.detail && (
                <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
                  {row.detail}
                </span>
              )}
            </button>
          ))}
          {!hits.length && remoteLoading && (
            <p style={{ margin: '2px', font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{t.saSearchingRegisterT}</p>
          )}
          {!hits.length && !loading && !remoteLoading && (
            // Not an error. The list is a minute old and the operator may be
            // holding something it has not caught up with, so their words go
            // to the server rather than being refused here.
            <p style={{ margin: '2px', font: '500 11px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>{t.saNoMatchT}</p>
          )}
        </div>
      )}
    </div>
  );
}
